import {PackModel} from 'models/configuration/PackModel'
import {RegistrationModel} from 'models/registration/RegistrationModel'
import {StepConfig, StepKey, StepModel} from 'models/registration/StepModel'
import {Memoize} from 'util/memoize'

export type SelectStepConfig = StepConfig

export class SelectStepModel extends StepModel {
	@Memoize()
	get key(): StepKey {
		return 'select'
	}

	@Memoize()
	get isApplicable(): boolean {
		return true
	}

	@Memoize()
	get isReady(): boolean {
		return this.registration.packs.length > 0
	}

	// ----- State changes ----- //
	togglePack(pack: PackModel): RegistrationModel {
		return this.registration.packsSet.has(pack)
			? this.deselectPack(pack)
			: this.selectPack(pack)
	}

	deselectPack(pack: PackModel): RegistrationModel {
		const newPacks = this.registration.packs.filter(p => p !== pack)
		const newTags = [...new Set(...newPacks.map(pack => pack.tagsSet))]
		return this.registration.update({
			packs: newPacks.map(p => p.key),
			tags: newTags.reduce((acc, curr) => ({...acc, [curr]: true}), {})
		})
	}

	selectPack(newPack: PackModel): RegistrationModel {
		const newPacks = [
			...this.registration.packs.filter(
				pack => !pack.tracks.find(track => newPack.tracksSet.has(track))
			),
			newPack
		]
		const newTags = [...new Set(...newPacks.map(pack => pack.tagsSet))]

		const result = this.registration.update({
			packs: newPacks.map(pack => pack.key),
			tags: newTags.reduce((acc, curr) => ({...acc, [curr]: true}), {})
		})

		if (result.isOnline) return result

		if (result.tracks.length > 1) {
			// Find a pack which contains all of the chosen tracks
			const bundle = this.event.packs.find(pack => {
				if (pack.isOnline) return false
				if (result.tracks.find(track => !pack.tracksSet.has(track)))
					return false
				return true
			})

			if (!bundle) {
				// No bundle found -> drop previously selected packages
				return this.registration.update({
					packs: [newPack.key],
					tags: [...newPack.tagsSet].reduce(
						(acc, curr) => ({...acc, [curr]: true}),
						{}
					)
				})
			}

			if (bundle.tracks.length === result.tracks.length) {
				// Bundle found with equal amount of tracks -> select that one instead
				return this.registration.update({
					packs: [bundle.key],
					tags: [...bundle.tagsSet].reduce(
						(acc, curr) => ({...acc, [curr]: true}),
						{}
					)
				})
			}
		}

		return result
	}
}
